/**
 * Returns: the queue and history, a lead's sales and returns, the reasons,
 * buyer requests and the returns report.
 *
 * A return is per buyer: one buyer of a multi-sold lead can return it while
 * the others' sales stand. Approving one changes the lead's revenue, so every
 * write here refreshes the lead, the lists and the money views.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components, paths } from './schema';

type S = components['schemas'];
export type LeadReturn = S['LeadReturn'];
export type ReturnsList = S['ReturnsList'];
export type ReturnsSettings = S['ReturnsSettings'];
export type ReturnReason = S['ReturnReason'];
export type LeadReturns = S['LeadReturns'];
export type ReturnableSale = S['ReturnableSale'];
export type ReturnsReport = S['ReturnsReport'];
export type ReturnsReportRow = S['ReturnsReportRow'];
export type ReturnsResult = S['ReturnsResult'];
export type ReturnsQuery = NonNullable<paths['/api/v1/returns']['get']['parameters']['query']>;
export type ReturnsGroupBy = 'buyer' | 'campaign' | 'supplier' | 'source' | 'reason';

/** Everything a return can change: the lead, its lists, and money. */
function refreshAfterReturn(qc: QueryClient) {
  for (const key of ['returns', 'lead', 'lead-returns', 'leads', 'dashboard', 'reports', 'entity-stats', 'lead-insights', 'statements']) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

/** The organisation's reasons, window and auto-approve. Anyone signed in reads them. */
export function useReturnsSettings(enabled = true) {
  return useQuery({
    queryKey: ['returns', 'settings'],
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/returns', {}), 'ReturnsSettings'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useSaveReturnsSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReturnsSettings) =>
      unwrap(await api.PUT('/api/v1/settings/returns', { body }), 'ReturnsSettings'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['returns'] }),
  });
}

/** The ledger: the queue (`status: requested`) or the history, filtered. */
export function useReturns(query: ReturnsQuery, enabled = true) {
  return useQuery({
    queryKey: ['returns', 'list', query],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/returns', { params: { query } }), 'ReturnsList'),
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
    enabled,
  });
}

/** One lead's sales (with each buyer's window) and its returns. */
export function useLeadReturns(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ['lead-returns', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/leads/{id}/returns', { params: { path: { id: id! } } }), 'LeadReturns'),
    enabled: enabled && !!id,
    retry: false,
  });
}

/** Staff: return leads for the named buyers, or every buyer each is sold to. */
export function useCreateReturns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S['CreateReturnsBody']) =>
      unwrap(await api.POST('/api/v1/returns', { body }), 'ReturnsResult'),
    onSuccess: () => refreshAfterReturn(qc),
  });
}

/** A buyer login asks to return a lead sold to it. */
export function useRequestReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S['RequestReturnBody']) =>
      unwrap(await api.POST('/api/v1/returns/requests', { body }), 'ReturnsResult'),
    onSuccess: () => refreshAfterReturn(qc),
  });
}

/** Staff: approve or reject requested returns. */
export function useDecideReturns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: S['DecideReturnsBody']) =>
      unwrap(await api.POST('/api/v1/returns/decide', { body }), 'ReturnsResult'),
    onSuccess: () => refreshAfterReturn(qc),
  });
}

export function useReturnsReport(groupBy: ReturnsGroupBy, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['reports', 'returns', groupBy, from, to],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/reports/returns', { params: { query: { group_by: groupBy, from, to } } }),
        'ReturnsReport',
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** "12.5%", or a dash when there were no sales to measure against. */
export function formatRate(rate: number | null | undefined): string {
  if (rate == null) return '—';
  const pct = rate * 100;
  return `${pct >= 10 || pct === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}
